import {
  ArrowRight,
  BellRing,
  BrainCircuit,
  Cable,
  ClipboardCheck,
  FileOutput,
  Files,
  Gauge,
  AppWindow,
  LayoutDashboard,
  ListChecks,
  Network,
  RefreshCcw,
  Workflow,
} from "lucide-react";
import { SiteNav } from "../components/SiteNav";
import { useI18n, type Language } from "../i18n/i18n";
import { homeMessages } from "../i18n/messages";
import { usePageMeta } from "../lib/meta";

type Bilingual = Record<Language, string>;

const frictionCards: Array<{ icon: typeof Files; title: Bilingual; body: Bilingual }> = [
  {
    icon: Files,
    title: { en: "Documents & data", it: "Documenti & dati" },
    body: { en: "Turn emails, forms, PDFs and documents into structured information without copying everything manually.", it: "Trasforma email, moduli, PDF e documenti in informazioni strutturate senza dover copiare tutto manualmente." },
  },
  {
    icon: FileOutput,
    title: { en: "Quotes & reports", it: "Preventivi & report" },
    body: { en: "Generate quotes, reports, documents or recurring outputs from information you already have.", it: "Genera preventivi, report, documenti o output ricorrenti partendo dalle informazioni che già utilizzi." },
  },
  {
    icon: ClipboardCheck,
    title: { en: "Requests & approvals", it: "Richieste & approvazioni" },
    body: { en: "Route requests to the right person, trigger approvals and keep important steps from being missed.", it: "Indirizza le richieste alla persona corretta, gestisci approvazioni e impedisci che passaggi importanti vengano dimenticati." },
  },
  {
    icon: Cable,
    title: { en: "Systems & integrations", it: "Sistemi & integrazioni" },
    body: { en: "Move information reliably between software, spreadsheets and tools that currently operate separately.", it: "Trasferisci informazioni in modo affidabile tra software, fogli di calcolo e strumenti che oggi lavorano separatamente." },
  },
  {
    icon: BellRing,
    title: { en: "Monitoring & alerts", it: "Monitoraggio & avvisi" },
    body: { en: "Track what matters and notify the right person automatically when something requires attention.", it: "Controlla ciò che conta e avvisa automaticamente la persona corretta quando qualcosa richiede attenzione." },
  },
  {
    icon: RefreshCcw,
    title: { en: "Repetitive administration", it: "Attività amministrative" },
    body: { en: "Reduce data entry, repeated updates and routine operational work.", it: "Riduci inserimento manuale dei dati, aggiornamenti ripetitivi e attività operative di routine." },
  },
];

const capabilities: Array<{ icon: typeof Gauge; title: Bilingual; body: Bilingual; note?: Bilingual; tags: Record<Language, string[]>; className: string }> = [
  {
    icon: LayoutDashboard,
    title: { en: "Business systems", it: "Sistemi aziendali" },
    body: { en: "When a spreadsheet or off-the-shelf tool no longer fits the process, I can build the software around it — from internal applications and dashboards to portals, tracking systems and data platforms.", it: "Quando un foglio di calcolo o un software standard non riesce più ad adattarsi al processo, posso costruire il sistema intorno a quel lavoro: applicazioni interne, dashboard, portali, sistemi di tracciabilità e piattaforme dati." },
    tags: { en: ["Internal tools", "Dashboards", "Tracking systems", "Data platforms"], it: ["Strumenti interni", "Dashboard", "Sistemi di tracciabilità", "Piattaforme dati"] },
    className: "systems-capability-business",
  },
  {
    icon: Workflow,
    title: { en: "Workflow automation", it: "Automazione dei processi" },
    body: { en: "I connect tools, information and people into reliable workflows that remove repetitive steps while keeping important decisions under human control.", it: "Collego strumenti, informazioni e persone in workflow affidabili che eliminano passaggi ripetitivi mantenendo sotto controllo le decisioni importanti." },
    tags: { en: ["Integrations", "Approvals", "Data flows", "Notifications"], it: ["Integrazioni", "Approvazioni", "Flussi di dati", "Notifiche"] },
    className: "systems-capability-workflow",
  },
  {
    icon: BrainCircuit,
    title: { en: "Applied AI", it: "AI applicata" },
    body: { en: "I put AI inside real workflows to read documents, extract and classify information, analyse data, prepare drafts and support faster decisions.", it: "Integro l’AI nei processi reali per leggere documenti, estrarre e classificare informazioni, analizzare dati, preparare contenuti e supportare decisioni più rapide." },
    note: { en: "AI is most useful when it becomes part of the process — not another disconnected tool.", it: "L’AI è più utile quando entra nel processo di lavoro, non quando diventa semplicemente un altro strumento separato." },
    tags: { en: ["Document understanding", "Extraction", "Classification", "Drafting", "Analysis"], it: ["Documenti", "Estrazione", "Classificazione", "Generazione", "Analisi"] },
    className: "systems-capability-ai",
  },
];

const scale = {
  en: ["One task", "A workflow", "An internal tool", "A complete business system"],
  it: ["Una attività", "Un workflow", "Uno strumento interno", "Un sistema aziendale completo"],
};

const scaleIcons = [ListChecks, Workflow, AppWindow, Network];

export function Automations() {
  const { language, text } = useI18n();
  const isItalian = language === "it";
  const title = isItalian ? "Sistemi Aziendali, Automazioni & AI — Mattia D’Elia" : "Business Systems, Automation & Applied AI — Mattia D’Elia";
  const description = isItalian
    ? "Software su misura, automazioni, integrazioni e AI applicata per ridurre il lavoro manuale e migliorare i processi aziendali."
    : "Custom software, workflow automation, integrations and applied AI designed to reduce manual work and improve business operations.";
  usePageMeta(
    title,
    description,
    "/automations",
    "/og-automations-v3.png",
    "Mattia D’Elia — business systems, automation and applied AI.",
  );

  return (
    <div className="catalogue-page automation-page">
      <SiteNav />
      <main className="automation-main" id="main">
        <section className="automation-hero" aria-labelledby="automation-title">
          <p className="home-eyebrow">{isItalian ? "Sistemi & Automazioni" : "Systems & Automation"}</p>
          <h1 id="automation-title">{isItalian ? "Software costruito intorno a come lavora davvero la tua azienda." : "Software built around how your business actually works."}</h1>
          <p>{isItalian ? "Sviluppo sistemi su misura, automazioni e AI applicata per ridurre il lavoro manuale, collegare le informazioni e rendere più affidabili le attività quotidiane." : "I build custom systems, automations and applied AI that reduce manual work, connect information and make everyday operations more reliable."}</p>
          <strong className="automation-scale-line">{isItalian ? "Da una singola attività ripetitiva a un sistema interno completo." : "From one repetitive task to a complete internal system."}</strong>
          <a className="home-button light" href="/#contact">{isItalian ? "Raccontami cosa ti fa perdere tempo" : "Tell me what takes too long"}<ArrowRight size={18} /></a>
        </section>

        <section className="friction-section" aria-labelledby="friction-title">
          <header className="systems-section-heading">
            <p className="home-eyebrow">{isItalian ? "Dove finisce il tempo?" : "Where does the time go?"}</p>
            <h2 id="friction-title">{isItalian ? "Partiamo dal lavoro che ripeti ogni giorno." : "Start with the work you repeat every day."}</h2>
            <p>{isItalian ? "Ogni azienda è diversa, ma gli stessi tipi di inefficienza si presentano continuamente." : "Every business is different, but the same kinds of friction appear again and again."}</p>
          </header>
          <div className="friction-grid">
            {frictionCards.map(({ icon: Icon, title: cardTitle, body }) => (
              <article key={cardTitle.en}><Icon aria-hidden="true" /><h3>{cardTitle[language]}</h3><p>{body[language]}</p></article>
            ))}
          </div>
          <p className="friction-closing">{isItalian ? "Se un’attività viene ripetuta continuamente, probabilmente esiste un modo migliore per gestirla." : "If a task happens repeatedly, there is probably a better way to handle it."}</p>
        </section>

        <section className="systems-capabilities" aria-labelledby="capabilities-title">
          <header className="systems-section-heading">
            <p className="home-eyebrow">{isItalian ? "Capacità principali" : "Core capabilities"}</p>
            <h2 id="capabilities-title">{isItalian ? "Tre modi per migliorare il lavoro dietro l’attività." : "Three ways to improve the work behind the business."}</h2>
          </header>
          <div className="systems-capability-list">
            {capabilities.map(({ icon: Icon, title: capabilityTitle, body, note, tags, className }) => (
              <article className={`systems-capability ${className}`} key={capabilityTitle.en}>
                <div className="systems-capability-marker"><Icon aria-hidden="true" /></div>
                <div className="systems-capability-copy">
                  <h3>{capabilityTitle[language]}</h3>
                  <p>{body[language]}</p>
                  {note && <strong>{note[language]}</strong>}
                  <ul>{tags[language].map((tag) => <li key={tag}>{tag}</li>)}</ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="system-scale-section" aria-labelledby="system-scale-title">
          <header className="systems-section-heading">
            <p className="home-eyebrow">{isItalian ? "Da una attività a un sistema completo" : "From one task to a complete system"}</p>
            <h2 id="system-scale-title">{isItalian ? "Costruisco ciò che il problema richiede davvero." : "Build what the problem actually needs."}</h2>
            <p>{isItalian ? "Alcuni problemi richiedono una semplice automazione. Altri hanno bisogno di software su misura, database, dashboard, integrazioni o un’applicazione completa. La soluzione deve adattarsi al problema, senza aggiungere complessità inutile." : "Some problems need a simple automation. Others need custom software, databases, dashboards, integrations or a complete application. The solution should match the problem — without adding unnecessary complexity."}</p>
          </header>
          <ol className="system-scale-flow">
            {scale[language].map((step, index) => {
              const ScaleIcon = scaleIcons[index];
              return <li key={step}><ScaleIcon className="system-scale-icon" aria-hidden="true" /><strong>{step}</strong>{index < scale[language].length - 1 && <ArrowRight className="system-scale-arrow" aria-hidden="true" />}</li>;
            })}
          </ol>
        </section>

        <section className="automation-closing">
          <p className="home-eyebrow">{isItalian ? "Un punto di partenza concreto" : "A practical starting point"}</p>
          <h2>{isItalian ? "Non serve automatizzare tutto. Basta partire dal punto giusto." : "You don’t need to automate everything. Start where it matters."}</h2>
          <p>{isItalian ? "Parto dalla parte del processo che richiede troppo tempo, genera lavoro ripetitivo o crea errori evitabili. Poi costruisco una soluzione mirata intorno a quel problema." : "I start by identifying the part of the process that takes too much time, creates unnecessary repetition or causes avoidable mistakes. Then I build a focused solution around it."}</p>
          <a className="home-button dark" href="/#contact">{isItalian ? "Parliamo del tuo processo" : "Discuss your process"}<ArrowRight size={18} /></a>
        </section>
      </main>
      <footer className="home-footer"><span>© {new Date().getFullYear()} Mattia D’Elia</span><span>{text(homeMessages.vat)}</span></footer>
    </div>
  );
}
